import 'whatwg-fetch'

import { ErrorType } from 'data/App/types'

import { getCSRFToken, getJWTToken, saveCSRFToken } from './tokenHelper'

type QueryType = Record<string, string>

type Request = {
  auth?: boolean
  body?: any
  method?: 'GET' | 'POST' | 'DELETE' | 'PUT'
  queryParams?: QueryType
  url: string
}

type ErrorResponse = { error: ErrorType; ok: false }
type SuccessResponse<T> = { ok: true; response: T }
export type Response<T> = ErrorResponse | SuccessResponse<T>

const queryStringBuilder = (parameters: QueryType) =>
  Object.keys(parameters).reduce((acc: string, key) => {
    const nextKeyValue = `${key}=${encodeURIComponent(String(parameters[key]))}`
    return `${acc ? `${acc}&` : acc}${nextKeyValue}`
  }, '')

const BASE_HEADERS = {
  Accept: 'application/json',
  'Blockchain-Origin': 'pit'
}

export const requestBuilder = ({
  auth,
  body,
  method,
  queryParams,
  url
}: Request): { options: RequestInit; url: string } => {
  const preHeaders =
    method === 'POST' || method === 'PUT'
      ? {
          ...BASE_HEADERS,
          'Content-Type': 'application/json',
          'X-App-Version': '1.0',
          'X-Client-Type': 'Browser',
          'X-Device-Id': '1'
        }
      : BASE_HEADERS

  const csrfToken = getCSRFToken()
  const JWT = getJWTToken()

  const headers =
    auth && csrfToken && JWT
      ? {
          ...preHeaders,
          Authorization: `Bearer ${JWT}`,
          'blockchain-csrf': csrfToken
        }
      : auth && csrfToken
      ? {
          ...preHeaders,
          'blockchain-csrf': csrfToken
        }
      : auth && JWT
      ? { ...preHeaders, Authorization: `Bearer ${JWT}` }
      : preHeaders

  return {
    options: {
      body: body ? JSON.stringify(body) : null,
      credentials: auth ? 'include' : 'same-origin',
      headers,
      method
    },
    url: queryParams ? `${url}?${queryStringBuilder(queryParams)}` : url
  }
}

const getErrorCause = (error) => {
  try {
    return JSON.parse(error == null ? '' : error)
  } catch (e) {
    return error
  }
}

const fetchApi = async <T>(rawRequest: Request): Promise<Response<T>> => {
  const { auth = false, body, method, queryParams, url } = rawRequest

  if (window.mocks?.enabled) {
    const normalizedUrl = url.replace('https://api.staging.blockchain.info', '')
    const res = window.mocks.resolve<T>(normalizedUrl, method ?? 'GET', body)
    // TODO setTimeout
    return Promise.resolve({ ok: true, response: res })
  }

  const request = requestBuilder({
    auth,
    body,
    method: method ?? 'GET',
    queryParams,
    url: url ?? '/'
  })

  try {
    const response = await fetch(request.url, request.options)
    if (!response.ok) {
      const { cause, code, description } = await response.json()

      return Promise.resolve({
        error: {
          cause: getErrorCause(cause ?? description),
          code,
          status: response.status
        },
        ok: false
      })
    }

    const token = response.headers.get('blockchain-csrf')
    if (token) {
      saveCSRFToken(token)
    }

    try {
      const parsedResponse = await response.json()
      return { ok: true, response: parsedResponse }
    } catch (error) {
      return { ok: true, response: {} as T }
    }
  } catch (error) {
    // @ts-ignore error can have a message!
    const msg = error && error.message ? error.message : 'Request failed'
    return Promise.resolve({ error: msg, ok: false })
  }
}

export default fetchApi
